import PageLoader from "components/PageLoader"
import { type ChangeEvent, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { getTintVTOService } from "services/tintvto.ts"
import { typedEnv } from "../../config/typedEnv"
import { Check, Download, Eye, Loader2, Plus, Search, Trash2, Upload, X } from "lucide-react"
import allLangs from "./allLangs.ts"
import { useAlert } from "providers/AlertProvider"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

interface LanguageOption {
  lang: string
  code: string
}

interface LanguageConfig {
  code: string
  list: Record<string, string>
}

type TranslationsData = Record<string, LanguageConfig>

// --- Sub-components ---

const AddLangDialog = ({
  onClose,
  onAddLang,
  isOpen,
  langList,
}: {
  onClose: () => void
  onAddLang: (payload: LanguageOption) => void
  isOpen: boolean
  langList: LanguageOption[]
}) => {
  const [selected, setSelected] = useState<LanguageOption | null>(null)
  const { t } = useTranslation()

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
          setSelected(null)
        }
      }}
    >
      <DialogContent data-testid="translates-add-language-dialog">
        <DialogHeader>
          <DialogTitle>{t("translatesPage.addNewLang")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label>{t("translatesPage.languagesLabel")} *</Label>
          <p className="text-xs text-muted-foreground">{t("translatesPage.languagesHint")}</p>
          <Select
            value={selected?.code}
            onValueChange={(code) => {
              const match = langList.find((item) => item.code === code)
              if (match) setSelected(match)
            }}
          >
            <SelectTrigger data-testid="translates-language-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent data-testid="translates-language-options">
              {langList.map(({ lang, code }) => (
                <SelectItem
                  key={code}
                  value={code}
                  data-testid={`translates-language-option-${code}`}
                >
                  {lang.charAt(0).toUpperCase() + lang.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="translates-add-language-cancel">
            {t("cancel")}
          </Button>
          <Button
            onClick={() => {
              if (selected) {
                onAddLang(selected)
                setSelected(null)
              }
            }}
            disabled={!selected}
            data-testid="translates-add-language-confirm"
          >
            <Check className="mr-1.5 size-3.5" />
            {t("translatesPage.addString")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const AddStringDialog = ({
  onClose,
  onAddString,
  isOpen,
}: {
  onClose: () => void
  onAddString: (key: string) => void
  isOpen: boolean
}) => {
  const [key, setKey] = useState("")
  const { t } = useTranslation()

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
          setKey("")
        }
      }}
    >
      <DialogContent data-testid="translates-add-string-dialog">
        <DialogHeader>
          <DialogTitle>{t("translatesPage.addNewString")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label htmlFor="stringKey">{t("translatesPage.stringKeyLabel")}</Label>
          <p className="text-xs text-muted-foreground">{t("translatesPage.stringKeyHint")}</p>
          <Input
            id="stringKey"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            data-testid="translates-string-key-input"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="translates-add-string-cancel">
            {t("cancel")}
          </Button>
          <Button
            onClick={() => {
              onAddString(key)
              setKey("")
            }}
            disabled={!key || key.length < 5}
            data-testid="translates-add-string-confirm"
          >
            <Check className="mr-1.5 size-3.5" />
            {t("translatesPage.addString")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// --- Main ---

const BASE_LANG = "english"

export const TranslatesPage = () => {
  const { showAlert } = useAlert()
  const { t } = useTranslation()
  const service = getTintVTOService()
  const hiddenFileInput = useRef<HTMLInputElement | null>(null)

  const [loading, setLoading] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [data, setData] = useState<TranslationsData>({})
  const [currentLang, setCurrentLang] = useState(BASE_LANG)
  const [search, setSearch] = useState("")

  // Dialogs
  const [addLangOpen, setAddLangOpen] = useState(false)
  const [addStringOpen, setAddStringOpen] = useState(false)
  const [deleteStringKey, setDeleteStringKey] = useState<string | null>(null)
  const [deleteLang, setDeleteLang] = useState<string | null>(null)

  const langList = useMemo(() => Object.keys(data).sort((a) => (a === BASE_LANG ? -1 : 1)), [data])

  const availableLangs = allLangs as LanguageOption[]

  const currentLangCode = data[currentLang]?.code ?? "en"

  // Filtered string keys based on search
  const stringKeys = useMemo(() => {
    const keys = Object.keys(data[currentLang]?.list ?? {})
    if (!search) return keys
    const q = search.toLowerCase()
    return keys.filter((key) => {
      if (key.toLowerCase().includes(q)) return true
      const val = data[currentLang]?.list[key]
      return val?.toLowerCase().includes(q)
    })
  }, [data, currentLang, search])

  // --- Data loading ---

  useEffect(() => {
    ;(async () => {
      try {
        setLoading(true)
        const { data: result } = await service.getTranslates()
        const configs: string[] = Array.isArray(result?.loadLangConfigs)
          ? (result.loadLangConfigs as string[])
          : []
        const parsed = configs.reduce((acc: TranslationsData, raw) => {
          const config = JSON.parse(raw) as LanguageConfig
          const match = availableLangs.find((l) => l.code === config.code)
          if (!match) return acc
          return { ...acc, [match.lang]: config }
        }, {})
        setData(parsed)
      } catch {
        showAlert({ error: true, text: t("translatesPage.importError") })
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  // --- Handlers ---

  const onSave = async () => {
    setSaveLoading(true)
    await service.saveTranslates(JSON.stringify(data))
    setSaveLoading(false)
    showAlert({ error: false, text: t("translatesPage.saved") })
  }

  const onExport = () => {
    if (!data[BASE_LANG]) return
    const escapeCsv = (val: string) => {
      if (val.includes(",") || val.includes('"') || val.includes("\n"))
        return `"${val.replace(/"/g, '""')}"`

      return val
    }
    const rows = [
      ["string", ...langList].map(escapeCsv).join(","),
      ...Object.keys(data[BASE_LANG].list).map((key) =>
        [key, ...langList.map((lang) => data[lang]?.list[key] ?? "")].map(escapeCsv).join(",")
      ),
    ]
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "translates.csv"
    link.click()
    URL.revokeObjectURL(url)
  }

  const onImport = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.readAsText(file)
    reader.onload = () => {
      try {
        const rows = reader.result
          ?.toString()
          .split("\n")
          .filter(Boolean)
          .map((line) => line.split(","))
        if (!rows || rows.length < 2) return
        const langs = rows[0].slice(1)
        const entries = rows.slice(1)
        const newData = langs.reduce<TranslationsData>((acc, lang, idx) => {
          const code = availableLangs.find((l) => l.lang === lang)?.code ?? ""
          return {
            ...acc,
            [lang]: {
              code,
              list: entries.reduce<Record<string, string>>(
                (listAcc, row) => ({ ...listAcc, [row[0]]: row[idx + 1] ?? "" }),
                {}
              ),
            },
          }
        }, {})
        setData(newData)
        showAlert({ error: false, text: t("translatesPage.imported") })
      } catch {
        showAlert({ error: true, text: t("translatesPage.importError") })
      }
    }
    // Reset input so same file can be re-imported
    e.target.value = ""
  }

  const onAddLang = ({ lang, code }: LanguageOption) => {
    setData((prev) => ({
      ...prev,
      [lang]: {
        code,
        list: Object.keys(prev[BASE_LANG]?.list ?? {}).reduce<Record<string, string>>(
          (acc, key) => ({ ...acc, [key]: "" }),
          {}
        ),
      },
    }))
    setAddLangOpen(false)
    setCurrentLang(lang)
  }

  const onDeleteLang = (lang: string) => {
    setData((prev) => {
      const next = { ...prev }
      delete next[lang]
      return next
    })
    if (currentLang === lang) setCurrentLang(BASE_LANG)
    setDeleteLang(null)
  }

  const onAddString = (key: string) => {
    setData((prev) => {
      const next = { ...prev }
      for (const lang of Object.keys(next))
        next[lang] = { ...next[lang], list: { ...next[lang].list, [key]: "" } }

      return next
    })
    setAddStringOpen(false)
  }

  const onDeleteString = (key: string) => {
    setData((prev) => {
      const next = { ...prev }
      for (const lang of Object.keys(next)) {
        const { [key]: _, ...rest } = next[lang].list
        next[lang] = { ...next[lang], list: rest }
      }
      return next
    })
    setDeleteStringKey(null)
  }

  const updateValue = (lang: string, key: string, value: string) => {
    setData((prev) => ({
      ...prev,
      [lang]: { ...prev[lang], list: { ...prev[lang].list, [key]: value } },
    }))
  }

  return (
    <div data-testid="translates-page">
      {/* Dialogs */}
      <AddLangDialog
        isOpen={addLangOpen}
        onAddLang={onAddLang}
        onClose={() => setAddLangOpen(false)}
        langList={availableLangs.filter(({ lang }) => !langList.includes(lang))}
      />
      <AddStringDialog
        isOpen={addStringOpen}
        onAddString={onAddString}
        onClose={() => setAddStringOpen(false)}
      />
      <AlertDialog open={!!deleteStringKey} onOpenChange={() => setDeleteStringKey(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("translatesPage.deleteStringTitle", "Delete string")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "translatesPage.deleteStringDescription",
                `This will remove "${deleteStringKey}" from all languages.`
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteStringKey && onDeleteString(deleteStringKey)}
            >
              {t("confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!deleteLang} onOpenChange={() => setDeleteLang(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("translatesPage.deleteLangTitle", "Delete language")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "translatesPage.deleteLangDescription",
                `This will remove all "${deleteLang}" translations.`
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteLang && onDeleteLang(deleteLang)}
            >
              {t("confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <input ref={hiddenFileInput} onChange={onImport} accept=".csv,text/csv" type="file" hidden />

      {/* Sticky header */}
      <div
        className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
        data-testid="translates-header"
      >
        <div className="flex h-14 items-center justify-between px-6">
          <h1 className="text-lg font-semibold" data-testid="translates-title">
            {t("translatesPage.title")}
          </h1>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAddStringOpen(true)}
              data-testid="translates-add-string"
            >
              <Plus className="mr-1.5 size-3.5" />
              {t("translatesPage.addString")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAddLangOpen(true)}
              data-testid="translates-add-language"
            >
              <Plus className="mr-1.5 size-3.5" />
              {t("translatesPage.addLang")}
            </Button>
            <Button variant="outline" size="sm" onClick={onExport} data-testid="translates-export">
              <Download className="mr-1.5 size-3.5" />
              {t("translatesPage.exportCsv")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => hiddenFileInput.current?.click()}
              data-testid="translates-import"
            >
              <Upload className="mr-1.5 size-3.5" />
              {t("translatesPage.importCsv")}
            </Button>
            <Button variant="outline" size="sm" asChild>
              <a
                href={`https://${typedEnv.VITE_TINT_WIDGET_LINK}/?lang=${currentLangCode}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="translates-preview"
              >
                <Eye className="mr-1.5 size-3.5" />
                {t("translatesPage.preview")}
              </a>
            </Button>
            <Button size="sm" onClick={onSave} disabled={saveLoading} data-testid="translates-save">
              {saveLoading ? (
                <Loader2 className="mr-1.5 size-3.5 animate-spin" />
              ) : (
                <Check className="mr-1.5 size-3.5" />
              )}
              {t("translatesPage.save")}
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {loading ? (
          <PageLoader />
        ) : (
          <Tabs value={currentLang} onValueChange={setCurrentLang}>
            <div className="mb-4 flex items-center justify-between gap-4">
              <TabsList data-testid="translates-tabs">
                {langList.map((lang) => (
                  <TabsTrigger
                    key={lang}
                    value={lang}
                    className="group capitalize"
                    data-testid={`translates-tab-${lang}`}
                  >
                    {lang}
                    {lang !== BASE_LANG && (
                      <button
                        type="button"
                        className="ml-1.5 hidden rounded-sm p-0.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive group-hover:inline-flex group-data-[state=active]:inline-flex"
                        onClick={(e) => {
                          e.stopPropagation()
                          setDeleteLang(lang)
                        }}
                      >
                        <X className="size-3" />
                      </button>
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>
              <div className="relative w-64">
                <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="translates-search"
                  placeholder={t("translatesPage.searchPlaceholder", "Search keys or values...")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-8 pl-8 text-sm"
                />
                {search && (
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setSearch("")}
                  >
                    <X className="size-3.5" />
                  </button>
                )}
              </div>
            </div>

            {langList.map((lang) => (
              <TabsContent key={lang} value={lang} data-testid={`translates-tab-panel-${lang}`}>
                <div className="rounded-md border" data-testid="translates-table">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="w-[35%] font-medium">
                          {t("translatesPage.stringKeyColumn")}
                        </TableHead>
                        <TableHead className="font-medium">
                          {t("translatesPage.stringValueColumn")}
                        </TableHead>
                        <TableHead className="w-10" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {stringKeys.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} className="h-24 text-center text-muted-foreground">
                            {search
                              ? t("translatesPage.noResults", "No matching strings found")
                              : t("translatesPage.noStrings", "No strings yet")}
                          </TableCell>
                        </TableRow>
                      ) : (
                        stringKeys.map((key) => (
                          <TableRow
                            key={key}
                            data-testid="translation-row"
                            data-translation-key={key}
                          >
                            <TableCell className="font-mono text-xs text-muted-foreground">
                              {key}
                            </TableCell>
                            <TableCell>
                              <Input
                                aria-label={key}
                                value={data[lang]?.list[key] ?? ""}
                                onChange={(e) => updateValue(lang, key, e.target.value)}
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-7 text-muted-foreground hover:text-destructive"
                                onClick={() => setDeleteStringKey(key)}
                              >
                                <Trash2 className="size-3.5" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  {stringKeys.length} {t("translatesPage.stringsCount", "strings")}
                  {search && ` (${t("translatesPage.filtered", "filtered")})`}
                </p>
              </TabsContent>
            ))}
          </Tabs>
        )}
      </div>
    </div>
  )
}
